import { useMediaQuery } from '@mui/material';
import { clearAlertsThunk } from 'actions/alerts/AlertsActions';
import { DatePickerSpinner } from 'components/Form/DatePickerSpinner/DatePickerSpinner';
import { SelectableTag } from 'components/Form/SelectableTag/SelectableTag';
import { Spinner } from 'components/UI/Spinner/Spinner';
import { useMarketplace } from 'hooks/useMarketplace';
import { useTimeZone } from 'hooks/useTimeZone';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { getOlbDateLimits } from 'reducers/masterData/MasterDataSelectors';
import { dispatch } from 'store';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { Pet } from 'types/Pet';
import { Service } from 'types/Service';
import { Staff } from 'types/Staff';
import {
    fetchStaffsAvailabilityThunk,
    getStaffNextAvailableDateThunk
} from 'actions/availability/AvailabilityActions';
import { GroomingAvailableHour } from 'types/Availability';
import { NEXT_AVAILABLE_DATE_WEEKS, OnlineBookingTime } from 'types/OnlineBooking';
import { getServiceDurationByPet } from 'utils/serviceUtils';
import classes from './DateTimePicker.module.scss';

interface DateTimePickerItem {
    pet: Pet;
    service?: Service;
    date?: Moment;
    // `true` only when the client clicked the day, not for the default / next available date
    dateSelectedByClient?: boolean;
    time?: OnlineBookingTime;
}

interface DateTimePickerProps {
    preferredStaff?: Staff;
    items: Array<DateTimePickerItem>;
    enableNextAvailableDate?: boolean;
    onChange: (updatedItems: Array<DateTimePickerItem>) => void;
}

const isSameTime = (time?: OnlineBookingTime, date?: Moment) => {
    if (!time || !date) return false;

    return date.hours() === time.hour && date.minutes() === time.minute;
};

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
    items,
    preferredStaff,
    enableNextAvailableDate = false,
    onChange
}) => {
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();
    const isMobile = useMediaQuery('mobile-and-tablet');
    const olbDateLimits = useSelector(getOlbDateLimits);

    const [availableHours, setAvailableHours] = React.useState<Array<GroomingAvailableHour>>([]);
    const [isNextAvailableDateFetched, setIsNextAvailableDateFetched] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    // Calculate minDate and maxDate based on marketplace preferences
    const limitPreferences = marketplace.onlineBookingPreferences.bookingDateLimits;
    const today = moment().tz(timeZone);

    const minDate = React.useMemo(
        () =>
            today
                .clone()
                .add(
                    olbDateLimits.from?.find(limit => limit.id === limitPreferences.from)
                        ?.offsetSeconds ?? 0,
                    'seconds'
                ),
        [today, olbDateLimits, limitPreferences.from]
    );

    const maxDate = React.useMemo(
        () =>
            today
                .clone()
                .add(
                    olbDateLimits.to?.find(limit => limit.id === limitPreferences.to)
                        ?.offsetSeconds ?? 0,
                    'seconds'
                ),
        [today, olbDateLimits, limitPreferences.to]
    );

    // Get the first item's date and time for display
    const selectedDate = items[0]?.date;
    const selectedTime = items[0]?.time;

    const hasAvailabilityBasedServices = React.useMemo(() => {
        return items.some(
            item =>
                item.service?.businessArea === MarketplaceServiceCategory.GROOMING ||
                item.service?.businessArea === MarketplaceServiceCategory.DENTAL ||
                item.service?.businessArea === MarketplaceServiceCategory.EVALUATION
        );
    }, [items]);

    const morningHours = React.useMemo(() => {
        return availableHours.filter(hour => hour.datetime.hour() < 12);
    }, [availableHours]);

    const afternoonHours = React.useMemo(() => {
        return availableHours.filter(hour => hour.datetime.hour() >= 12);
    }, [availableHours]);

    const dateChangeHandler = React.useCallback(
        async (date: Moment, selectedByClient = false) => {
            if (!date.isSame(selectedDate, 'day')) {
                dispatch(clearAlertsThunk());
            }

            let hours: Array<GroomingAvailableHour> = [];
            let finalDate = date;

            if (hasAvailabilityBasedServices) {
                setIsLoading(true);

                try {
                    // Calculate parameters for availability API
                    const petsCount = items.length;

                    // Calculate total duration based on all services
                    const durationInMinutes = items.reduce((totalDuration, item) => {
                        if (!item.service) return totalDuration;

                        const serviceDuration = getServiceDurationByPet(
                            item.service,
                            item.pet.type,
                            item.pet.size
                        );

                        return totalDuration + (serviceDuration || 0);
                    }, 0);

                    // Get staff IDs that can perform ALL selected services (intersection).
                    // Otherwise the availability API may return slots where a staff is available
                    // but cannot perform one of the services, leading to bookings with no staff
                    // assigned (SU-2105).
                    const serviceStaffLists = items
                        .map(item => item.service?.staffIds)
                        .filter(
                            (ids): ids is Array<number> => Array.isArray(ids) && ids.length > 0
                        );

                    let staffIds =
                        serviceStaffLists.length > 0
                            ? serviceStaffLists.reduce((acc, ids) =>
                                  acc.filter(id => ids.includes(id))
                              )
                            : [];

                    if (preferredStaff) {
                        staffIds = [preferredStaff?.id];
                    }

                    // Use staff availability for grooming/evaluation services
                    const availability = await dispatch(
                        fetchStaffsAvailabilityThunk(date, staffIds, petsCount, durationInMinutes)
                    );
                    hours = availability[0]?.hours || [];

                    // Handle next available date logic
                    if (
                        enableNextAvailableDate &&
                        hours.length === 0 &&
                        !isNextAvailableDateFetched &&
                        (!selectedDate || selectedDate.isSame(minDate, 'day'))
                    ) {
                        const nextDate = await dispatch(
                            getStaffNextAvailableDateThunk(
                                date,
                                petsCount,
                                durationInMinutes,
                                NEXT_AVAILABLE_DATE_WEEKS,
                                staffIds
                            )
                        );

                        finalDate = nextDate.date;
                        hours = nextDate.hours;
                    }
                } catch (error) {
                    console.error('Error fetching availability:', error);
                } finally {
                    setIsLoading(false);
                }
            }

            const updatedItems = items.map(item => ({
                ...item,
                date: finalDate.clone(),
                // The mount re-run keeps the client's pick as long as it lands on the same day
                dateSelectedByClient:
                    selectedByClient ||
                    (!!item.dateSelectedByClient && finalDate.isSame(item.date, 'day')),
                time: undefined // Reset time when date changes
            }));

            setAvailableHours(hours);
            setIsNextAvailableDateFetched(true);
            onChange(updatedItems);
        },
        [
            items,
            hasAvailabilityBasedServices,
            selectedDate,
            preferredStaff,
            minDate,
            isNextAvailableDateFetched,
            enableNextAvailableDate,
            onChange
        ]
    );

    const timeChangeHandler = React.useCallback(
        (hour: GroomingAvailableHour) => {
            let currentTime = hour.datetime.clone();
            const availableStaffId = hour.staffAvailable[0];

            const updatedItems = items.map(item => {
                const timeForThisItem: OnlineBookingTime = {
                    hour: currentTime.hours(),
                    minute: currentTime.minutes(),
                    label: currentTime.format('h:mm A'),
                    staffId: availableStaffId
                };

                // Calculate duration for this service and add it to the next start time
                if (item.service) {
                    const serviceDuration = getServiceDurationByPet(
                        item.service,
                        item.pet.type,
                        item.pet.size
                    );

                    if (serviceDuration) {
                        currentTime = currentTime.add(serviceDuration, 'minutes');
                    }
                }

                return {
                    ...item,
                    time: timeForThisItem
                };
            });

            onChange(updatedItems);
        },
        [items, onChange]
    );

    // Initialize availability on first render
    React.useEffect(() => {
        if (!hasAvailabilityBasedServices) return;

        const defaultDate = selectedDate || minDate;
        if (defaultDate) {
            dateChangeHandler(defaultDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    return (
        <>
            {selectedDate && (
                <DatePickerSpinner
                    mode={isMobile ? 'month' : 'week'}
                    minDate={minDate}
                    maxDate={maxDate}
                    timeZone={timeZone}
                    selectedDate={selectedDate}
                    onChange={dateChangeHandler}
                />
            )}

            {isLoading && (
                <div className="w-full text-center mt-6">
                    <Spinner size={50} />
                </div>
            )}

            {!isLoading && availableHours.length === 0 && (
                <div className={classes.timeGroup}>
                    <p className={classes.noAvailability}>Not available</p>
                </div>
            )}

            {!isLoading && availableHours.length > 0 && (
                <>
                    <div className={classes.timeGroup}>
                        <p className={classes.timeGroupLabel}>Morning</p>

                        {morningHours.length === 0 && (
                            <p className={classes.noAvailability}>Not available</p>
                        )}

                        <div className={classes.timeGrid}>
                            {morningHours.map(hour => (
                                <SelectableTag
                                    key={hour.time}
                                    className={classes.timeTag}
                                    selected={isSameTime(selectedTime, hour.datetime)}
                                    onClick={() => timeChangeHandler(hour)}
                                >
                                    {hour.time}
                                </SelectableTag>
                            ))}
                        </div>
                    </div>

                    <div className={classes.timeGroup}>
                        <p className={classes.timeGroupLabel}>Afternoon</p>

                        {afternoonHours.length === 0 && (
                            <p className={classes.noAvailability}>Not available</p>
                        )}

                        <div className={classes.timeGrid}>
                            {afternoonHours.map(hour => (
                                <SelectableTag
                                    key={hour.time}
                                    className={classes.timeTag}
                                    selected={isSameTime(selectedTime, hour.datetime)}
                                    onClick={() => timeChangeHandler(hour)}
                                >
                                    {hour.time}
                                </SelectableTag>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export type { DateTimePickerItem };
